import { Box, FormLabel, Stack, Typography } from '@mui/joy';
import { Plus, Warning } from '@phosphor-icons/react';
import { isEmpty } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, FormField, IManagedNote, Notes } from 'core/components';
import { NotificationElement } from 'core/components/Notification';
import { ApiStatus, InputType, NotificationPosition } from 'core/enums';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext, useStepsContext } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { preventEnter } from 'core/utils';
import { AdvancedPartNumberFormField, ManufacturerFormField } from 'parts/elements';
import { QuotePartNoteAddButton } from 'quotes/components/QuotePartAddNote';
import { PricingStrategyFormField } from 'quotes/elements';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { ICustomerQuote, IQuotePart, IQuotePartLineForm } from 'quotes/types';
import { getCostInputProps } from 'quotes/utils';
import { Color, Direction, Level } from 'theme/enums';

import { CostSection, usePricingStateContext } from './CostSection';
import { PriceSection } from './PriceSection';
import { useUpsertLineFormContext } from './state';
import { useNotes } from './useNotes';
import { hasLineFormChanges } from './utils';

interface IProps {
  quoteId: number;
  quoteType?: QuoteType;
  quotePart: IQuotePart;
  isEdit: boolean;
  onClose: () => void;
  isAlternateVersionEnabled?: boolean;
  entityType?: QuoteEntityType;
  quote: ICustomerQuote;
  isPartLocked?: boolean;
  getInitialValues?: () => Partial<IQuotePartLineForm> | null;
}

export const LineDetailsForm = forwardRef(function LineDetailsForm(
  {
    quoteId,
    quoteType = QuoteType.CUSTOMER,
    quotePart,
    isEdit,
    onClose,
    isAlternateVersionEnabled = false,
    entityType,
    quote,
    isPartLocked = false,
    getInitialValues,
  }: IProps,
  ref
) {
  const { t } = useTranslation();

  const { activeStep } = useStepsContext();
  const { control, errors, watch, trigger, getValues, setValue } = useFormContext();

  const { onChangePricingStrategy } = usePricingStateContext();

  // Vendor Unit Cost writes `quote_part.price`, and the BE rounds that column with the line's
  // PRICE decimals -- QuotationPartService::processPricingData keys the scale off the column
  // name, and `price` is not a costing field. Cost decimals here promised precision the save
  // silently dropped (5.555 came back 5.56). Edit-only: Add Line has no line yet, so there is
  // no resolved precision to promise.
  const priceDecimals = quotePart?.quotePriceDecimals;

  const { focusElementName, setFocusElementName } = useFocusInputFunctionality();

  const {
    hintOpen,
    closeHint,
    verifiedPart,
    isValidating,
    uomOptions,
    focusInput,
    setFocusInput,
    emptyPartValues,
    isSubmitting,
    onChangePartNumber,
    onKeyDownPartNumber,
    onChangeManufacturer,
    onChangeQuantity,
    onBlurQuantity,
    onChangeUom,
    submitFormOnEnter,
    quantityWarnings,
  } = useUpsertLineFormContext();

  const partDetailsPayload = useMemo(
    () => ({
      accountsReceivableId: quote.billToAddress?.accountsReceivableId,
      salesExternalLocationId: quote.location?.externalLocationId,
    }),
    [quote.billToAddress?.accountsReceivableId, quote.location?.externalLocationId]
  );

  useEffect(() => {
    if (activeStep && activeStep.type !== 'details') {
      return;
    }

    // A failed Next has put the focus on the invalid field; the move to the next field to fill
    // (after pricing, after part validation) is dropped rather than deferred, or it would fire
    // once the user fixes the error.
    if (focusInput && !isEmpty(errors)) {
      setFocusInput(null);

      return;
    }

    if (focusInput && focusInput !== focusElementName) {
      setFocusElementName(focusInput);
      setFocusInput(null);
    }

    return () => setFocusElementName(null);
  }, [focusInput, focusElementName, setFocusElementName, setFocusInput, activeStep, errors]);

  useImperativeHandle(
    ref,
    () => ({
      triggerFormSubmit: () => {
        return new Promise((resolve, reject) => {
          trigger()
            .then((isValid) => {
              if (isValid) {
                resolve(getValues());
              } else {
                reject();
              }
            })
            .catch(reject);
        });
      },
      hasChanges: () => hasLineFormChanges(getValues(), getInitialValues?.() ?? null),
    }),
    [getInitialValues, getValues, trigger]
  );

  const partValue = watch('part');
  const manufacturerId = watch('manufacturer')?.id;
  const notesValue = watch('notes');

  const { onAddNote, onDeleteNote, onEditNote, hasUndiscardableNote } = useNotes({
    notes: notesValue,
    setNotes: (value: IManagedNote[]) => setValue('notes', value),
    entityType,
    isAlternateVersionEnabled,
  });

  const quoteParts = useMemo(() => [quotePart], [quotePart]);

  const notification = {
    title: t('titleHint'),
    message: t('messageHintAddNewPartLine'),
    type: ApiStatus.INFO,
    position: NotificationPosition.DRAWER,
  };

  return (
    <Form onClose={onClose} isOpen inline withoutButtons withoutNotification>
      <Box sx={{ display: 'flex', flexDirection: Direction.COLUMN, gap: 2 }}>
        <NotificationElement
          {...notification}
          showNotification={hintOpen && !(watch('description') || partValue) && !isValidating}
          onClose={closeHint}
        />

        <AdvancedPartNumberFormField
          partSearchPayload={{ quoteId }}
          partAutocompletePayload={{ quoteId }}
          onChangePartNumber={onChangePartNumber}
          onKeyDownPartNumber={onKeyDownPartNumber}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
          disabled={isValidating || isSubmitting || isPartLocked}
          level={1}
          partDetailsPayload={partDetailsPayload}
        />

        <ManufacturerFormField
          key={manufacturerId}
          name="manufacturer"
          onChange={onChangeManufacturer}
          onBlur={onChangeManufacturer}
          disabled={verifiedPart || isValidating || isSubmitting || isPartLocked}
          isAlternateVersionEnabled={isAlternateVersionEnabled}
        />

        <FormField
          name="description"
          type={InputType.TEXTAREA}
          inputProps={{
            control: control,
            error: errors?.description,
            label: t('labelDescription'),
            placeholder: t('placeholderUpsertLineDescription'),
            maxRows: 6,
            onKeyDown: preventEnter,
            disabled: verifiedPart || isValidating || isSubmitting,
            required: true,
          }}
        />

        <Stack direction={Direction.ROW} gap="inherit">
          <FormField
            name="quantity"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              control: control,
              error: errors?.quantity,
              label: t('labelQuantity'),
              placeholder: t('placeholderUpsertLineQuantity'),
              color: quantityWarnings?.length
                ? Color.WARNING
                : errors?.quantity
                ? Color.DANGER
                : Color.NEUTRAL,
              startDecorator: quantityWarnings?.length ? <Warning weight="fill" /> : null,
              onChange: onChangeQuantity,
              onBlur: onBlurQuantity,
              onKeyDown: submitFormOnEnter,
              disabled: emptyPartValues || isSubmitting,
              helperTexts: quantityWarnings?.map((warning, index) => (
                <Typography
                  key={`quantityWarning${index}`}
                  level={Level.BODY_XS}
                  color={Color.WARNING}
                >
                  {warning}
                </Typography>
              )),
              slotProps: {
                input: {
                  min: 0,
                  thousandSeparator: true,
                },
              },
              required: true,
            }}
          />

          <FormField
            name="uom"
            type={InputType.SELECT}
            inputProps={{
              control: control,
              error: errors?.uom,
              label: t('labelUom'),
              placeholder: t('placeholderUpsertLineUom'),
              onChange: onChangeUom,
              getLabel: (option: IAutocompleteOption) => option?.name,
              getValue: (option: IAutocompleteOption) => option?.name,
              options: uomOptions,
              disabled: emptyPartValues || isSubmitting,
              required: true,
            }}
          />
        </Stack>

        {QuoteType.VENDOR === quoteType && isEdit && (
          <FormField
            name="price"
            type={InputType.NUMBER_INPUT}
            inputProps={{
              control: control,
              error: errors?.price,
              label: t('labelUnitCost'),
              placeholder: t('placeholderUpsertLineUnitCost'),
              onKeyDown: submitFormOnEnter,
              disabled: emptyPartValues || isSubmitting,
              ...getCostInputProps(priceDecimals),
            }}
          />
        )}

        {QuoteType.CUSTOMER === quoteType && (
          <>
            <CostSection
              quotePart={quotePart}
              quoteId={quoteId}
              isAlternateVersionEnabled={isAlternateVersionEnabled}
              isShipAndDebitVendor={partValue?.supplier?.isShipAndDebit || false}
              externalReplacementCost={partValue?.externalReplacementCost}
              externalRepCostRegistrationNum={partValue?.externalRepCostRegistrationNum}
            />

            {isAlternateVersionEnabled && (
              <PricingStrategyFormField
                onChange={onChangePricingStrategy}
                disabled={isSubmitting}
              />
            )}

            <PriceSection
              quote={quote}
              isAlternateVersionEnabled={isAlternateVersionEnabled}
              onPriceKeyDown={submitFormOnEnter}
            />

            {!isEdit && !emptyPartValues && (
              <Stack gap={0.5}>
                {notesValue?.length > 0 && <FormLabel>{t('labelNotes')}</FormLabel>}

                <Notes
                  notes={notesValue}
                  noteProps={{
                    onDeleteNote,
                    onSubmitEditNote: onEditNote,
                  }}
                />

                <QuotePartNoteAddButton
                  hasUndiscardableNote={hasUndiscardableNote}
                  onSubmit={onAddNote}
                  level={1}
                  entityType={entityType}
                  isAlternateVersionEnabled={isAlternateVersionEnabled}
                  quoteType={quoteType}
                  startDecorator={<Plus />}
                  sx={{ width: 'fit-content' }}
                />
              </Stack>
            )}

            {isEdit && (
              <QuotePartNoteAddButton
                quoteParts={quoteParts}
                level={1}
                entityType={entityType}
                isAlternateVersionEnabled={isAlternateVersionEnabled}
                quoteType={quoteType}
                startDecorator={<Plus />}
                sx={{ width: 'fit-content' }}
              />
            )}
          </>
        )}
      </Box>
    </Form>
  );
});
